import { z } from 'zod';
import { addDays } from './facilities-preventive.ts';
import { toScaled } from './money.ts';
import { decimalString } from './schemas.ts';

/**
 * Lançamentos previstos do caixa (ADR 0084, Entrega 4): a regra (valor, data e recorrência) e as
 * datas de cada ocorrência. Sem acesso ao banco: a mesma regra dá sempre as mesmas datas.
 */

export const FORECAST_FREQUENCIES = [
  'once',
  'weekly',
  'biweekly',
  'monthly',
  'quarterly',
  'semiannual',
  'yearly',
  'custom',
] as const;
export type ForecastFrequency = (typeof FORECAST_FREQUENCIES)[number];
export const FORECAST_NATURES = ['operating', 'investing', 'financing'] as const;
export const FORECAST_CERTAINTIES = ['confirmed', 'probable', 'estimated', 'scenario'] as const;
/** Recorrência personalizada: a cada N dias, semanas ou meses. */
export const FORECAST_CUSTOM_UNITS = ['day', 'week', 'month'] as const;
export type ForecastCustomUnit = (typeof FORECAST_CUSTOM_UNITS)[number];
export const FORECAST_DIRECTIONS = ['in', 'out', 'transfer'] as const;
/** Limite de ocorrências de uma regra (a recorrência sempre tem fim). */
export const MAX_FORECAST_OCCURRENCES = 120;

const MONTHS: Partial<Record<ForecastFrequency, number>> = {
  monthly: 1,
  quarterly: 3,
  semiannual: 6,
  yearly: 12,
};
const DAYS: Partial<Record<ForecastFrequency, number>> = { weekly: 7, biweekly: 14 };

/** Mesma data `n` meses depois; dia que não existe no mês cai no último (31 → 30, 29/02 → 28/02). */
export function addMonthsClamped(date: string, n: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const index = y * 12 + (m - 1) + n;
  const year = Math.floor(index / 12);
  const month = (index % 12) + 1;
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const pad = (v: number) => String(v).padStart(2, '0');
  return `${year}-${pad(month)}-${pad(Math.min(d, last))}`;
}

export interface ForecastRule {
  frequency: ForecastFrequency;
  customUnit?: ForecastCustomUnit | null;
  customEvery?: number | null;
  startDate: string;
  endDate?: string | null;
  maxOccurrences?: number | null;
}

/** Passo da regra: meses ou dias entre uma ocorrência e a seguinte. */
function step(rule: ForecastRule): { months: number } | { days: number } {
  if (rule.frequency === 'custom') {
    const every = rule.customEvery ?? 0;
    if (!Number.isInteger(every) || every < 1) throw new Error('Recorrência sem intervalo');
    if (rule.customUnit === 'month') return { months: every };
    return { days: every * (rule.customUnit === 'week' ? 7 : 1) };
  }
  const months = MONTHS[rule.frequency];
  return months !== undefined ? { months } : { days: DAYS[rule.frequency]! };
}

/**
 * Datas das ocorrências de uma regra: a primeira é a data de início; as seguintes contam sempre a
 * partir dela (o dia 31 volta a ser 31 nos meses que têm). Para na data final, no número máximo
 * ou no limite de 120.
 */
export function forecastDates(rule: ForecastRule): string[] {
  if (rule.frequency === 'once') return [rule.startDate];
  const max = Math.min(rule.maxOccurrences ?? MAX_FORECAST_OCCURRENCES, MAX_FORECAST_OCCURRENCES);
  const by = step(rule);
  const out: string[] = [];
  for (let i = 0; out.length < max; i++) {
    const date =
      'months' in by
        ? addMonthsClamped(rule.startDate, i * by.months)
        : addDays(rule.startDate, i * by.days);
    if (rule.endDate && date > rule.endDate) break;
    out.push(date);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Entradas da API
// ---------------------------------------------------------------------------

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida');
const amount = decimalString(15, 4).refine(
  (v) => toScaled(v) > 0n,
  'O valor precisa ser maior que zero',
);
const reason = z.string().trim().min(3).max(1000);
const optionalId = z.uuid().nullable().optional();

export const forecastInput = z
  .object({
    legalEntityId: z.uuid(),
    branchId: optionalId,
    direction: z.enum(FORECAST_DIRECTIONS),
    description: z.string().trim().min(3).max(300),
    amount,
    currency: z.string().regex(/^[A-Z]{3}$/, 'Moeda com 3 letras'),
    /** Mês de competência (AAAA-MM); sem ele, o mês da data prevista. */
    competence: z
      .string()
      .regex(/^\d{4}-\d{2}$/, 'Mês inválido')
      .nullable()
      .optional(),
    accountKey: optionalId,
    /** Transferência prevista: a conta que recebe. */
    toAccountKey: optionalId,
    expenseTypeId: optionalId,
    classificationId: optionalId,
    costCenterId: optionalId,
    partnerId: optionalId,
    nature: z.enum(FORECAST_NATURES).default('operating'),
    certainty: z.enum(FORECAST_CERTAINTIES).default('probable'),
    frequency: z.enum(FORECAST_FREQUENCIES).default('once'),
    customUnit: z.enum(FORECAST_CUSTOM_UNITS).nullable().optional(),
    customEvery: z.number().int().min(1).max(366).nullable().optional(),
    startDate: isoDate,
    endDate: isoDate.nullable().optional(),
    maxOccurrences: z.number().int().min(1).max(MAX_FORECAST_OCCURRENCES).nullable().optional(),
    notes: z
      .string()
      .trim()
      .max(2000)
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    responsibleId: optionalId,
  })
  .refine((f) => !f.expenseTypeId === !f.classificationId, {
    message: 'Informe o Tipo de Gasto e o Tipo de Classificação juntos',
    path: ['classificationId'],
  })
  .refine((f) => f.frequency === 'once' || !!f.endDate || !!f.maxOccurrences, {
    message: 'A recorrência precisa de data final ou número de ocorrências',
    path: ['endDate'],
  })
  .refine((f) => !f.endDate || f.endDate >= f.startDate, {
    message: 'A data final vem depois do início',
    path: ['endDate'],
  })
  .refine((f) => f.frequency !== 'custom' || (!!f.customUnit && !!f.customEvery), {
    message: 'Informe o intervalo da recorrência personalizada',
    path: ['customEvery'],
  })
  .refine(
    (f) =>
      f.direction !== 'transfer' ||
      (!!f.accountKey && !!f.toAccountKey && f.accountKey !== f.toAccountKey),
    { message: 'A transferência precisa de duas contas diferentes', path: ['toAccountKey'] },
  )
  .refine((f) => f.direction !== 'transfer' || (!f.expenseTypeId && !f.partnerId), {
    message: 'Transferência entre contas não tem classificação nem parceiro',
    path: ['classificationId'],
  });
export type ForecastInput = z.infer<typeof forecastInput>;

/** Editar uma ocorrência: só esta, ou esta e as seguintes (nova regra a partir dela). */
export const forecastOccurrenceEditInput = z
  .object({
    scope: z.enum(['this', 'following']),
    dueDate: isoDate.optional(),
    amount: amount.optional(),
  })
  .refine((e) => e.dueDate !== undefined || e.amount !== undefined, {
    message: 'Informe a nova data ou o novo valor',
    path: ['amount'],
  });

/** Vínculo com o registro real: a parcela do Contas a Pagar ou um recebimento (Entrega 5). */
export const forecastLinkInput = z
  .object({
    installmentId: z.uuid().optional(),
    receiptId: z.uuid().optional(),
    amount,
  })
  .refine((l) => !l.installmentId !== !l.receiptId, {
    message: 'Escolha a parcela ou o recebimento',
    path: ['installmentId'],
  });

export const forecastReasonInput = z.object({ reason });
